import { useForm, useFieldArray, Controller } from "react-hook-form"
import "../../ConfigPropinas.css"
import { useEffect, useRef, useState } from "react"
import Dropdown from "src/shared/components/forms/selelct-dropdown/Dropdown"
import InputNumber from "src/shared/components/forms/InputNumber/InputNumber"
import Icon from "src/shared/icons"
import { Button } from "src/shared/components/forms"
import {
    GetConfiguracionesPropinaQuery,
    useActualizarConfiguracionPropinaMutation,
    useGetEsquemaPuestosPropinasQuery,
    useGetPuestosCatalogoQuery,
    useConfigurarEsquemaPuestosPropinasMutation
} from "src/gql/schema"
import useSnackbar from "src/shared/hooks/useSnackbar"
import { useProfile } from "src/shared/hooks/useProfile"
import { puestosHospedaje, puestosRestaurante } from "src/constants/puestos"
import { RoleNames } from "src/shared/hooks/useAuth"
import Switch from "src/shared/components/forms/switch/Switch"
import { FormValues } from "./Pagos.types"
import { getDiffItems } from "./helpers/data"
import { sum } from "src/shared/helpers/calculator"

const areas = {
    Recepción: ["Recepcionista", "Valet parking", "Guardia de seguridad"],
    Hospedaje: puestosHospedaje,
    "Alimentos y Bebidas": puestosRestaurante,
    "Administración y operaciones": [
        "Gerente general",
        "Gerente operativo",
        "Contador",
        "Administrador",
        "Encargado de caja",
        "Auditor",
        "Mensajero",
        "Sistemas",
        "Compras",
        "Marketing",
        "Personal de recursos humanos",
    ],
}

type PagosProps = {
    config?: GetConfiguracionesPropinaQuery["configuraciones_propina"]
    refetchConfig: () => void
}

const Pagos = ({ config: fetchConfig, refetchConfig }: PagosProps) => {
    const { showSnackbar } = useSnackbar()
    const { rolName, hotel_id } = useProfile()
    const isRecepcionista = rolName === RoleNames.recepcionista

    const { data, refetch } = useGetEsquemaPuestosPropinasQuery({ variables: { hotel_id } })
    const { data: puestos } = useGetPuestosCatalogoQuery()
    const [load, setLoad] = useState<boolean>(true)
    const [errorMessage, setErrorMessage] = useState<string>("")
    const [error, setError] = useState<string>("")

    const snapshotAreas = useRef<FormValues["areas"]>([])

    const [actualizarConfiguracionPropina] = useActualizarConfiguracionPropinaMutation()
    const [ActualizarConfigPagos] = useConfigurarEsquemaPuestosPropinasMutation()

    const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
        defaultValues: {
            pago: false,
            areas: [],
        },
    })

    const { fields, append, remove } = useFieldArray({
        control,
        name: "areas",
    })

    const selectedRoles = watch("areas").map((area, index) => ({
        area: area.area,
        rol: area.rol,
        index,
    }))

    const isAreaFullySelected = (area: string) => {
        const roles = areas[area as keyof typeof areas]
        return roles.every((rol) => selectedRoles.some((selectedRole) => selectedRole.rol === rol))
    }

    useEffect(()=>{
        if (data) {
            if (data?.puestos_propinas?.length > 0) {
                const areas = data?.puestos_propinas?.map((p) => {
                    return {
                        area: p?.puesto?.area?.nombre || "",
                        rol: p?.puesto?.nombre || "",
                        porcentaje: `${p.porcentaje_propina || ""}`,
                        puesto_id: p?.puesto_id || "",
                        hotel_puesto_propina_id: p.hotel_puesto_propina_id || "",
                    }
                }) || []
                setValue("areas", areas)
                snapshotAreas.current = areas
            }
        }
    }, [data])

    useEffect(()=>{
        const config = fetchConfig?.[0]
        
        if (config) {
            setValue("pago", config?.paga_propina_en_turno || false)
        }
    }, [fetchConfig])

    useEffect(() => {
        if(data?.puestos_propinas && load) {
            setLoad(false)
        }
    }, [data, load])

    const selectedAreas = watch("areas")
    const lastField = selectedAreas[selectedAreas.length - 1]

    const handleAddField = () => {
        if (fields.length === 0) {
            append({ area: "", rol: "", porcentaje: "", puesto_id: "", hotel_puesto_propina_id: "" })
            setErrorMessage("")
        } else {
            const lastField = selectedAreas[selectedAreas.length - 1]
            if (!lastField.area || !lastField.rol || !lastField.porcentaje) {
                setErrorMessage("Por favor, completa todos los campos antes de agregar otro.")
                return
            }
            setErrorMessage("")
            append({ area: "", rol: "", porcentaje: "", puesto_id: "", hotel_puesto_propina_id: "" })
        }
    }

    const onSubmit = async (formData: FormValues) => {
        const totalPercentage = sum(formData.areas.map((v)=>Number(v.porcentaje)))
        if (totalPercentage > 100) {
            setError("Excediste el 100% del porcentaje a pagar")
            return
        }

        if (load) {
            return
        }

        setLoad(true)

        try {
            setError("")
            const config = fetchConfig?.[0]
            if(config?.paga_propina_en_turno !== formData.pago) {
                const {data: res} = await actualizarConfiguracionPropina({
                    variables: {
                        UpdateConfiguracionPropinaInput: {
                            configuracion_propina_id: config?.configuracion_propina_id || "",
                            paga_propina_en_turno: formData.pago
                        }
                    }
                })
                if (res?.actualizar_configuracion_propina) {
                    refetchConfig()
                }
            }

            const areas_modificadas = formData.areas.map((item)=>{
                const puesto_id = puestos?.puestos?.find((puesto) => puesto.nombre === item.rol)?.puesto_id || ""
                return {
                    ...item,
                    puesto_id
                }
            })

            const { creados, modificados, eliminados } = getDiffItems(snapshotAreas.current, areas_modificadas)

            const update = await ActualizarConfigPagos({
                variables: {
                    datos: {
                        hotel_id,
                        crear: creados.map(({puesto_id = "", porcentaje = ""})=>{
                            return {
                                puesto_id,
                                porcentaje_propina: Number(porcentaje)
                            }
                        }),
                        actualizar: modificados.map(({ hotel_puesto_propina_id = "", porcentaje = "" })=>{
                            return {
                                hotel_puesto_propina_id,
                                porcentaje_propina: Number(porcentaje)
                            }
                        }),
                        eliminar: eliminados.map(({hotel_puesto_propina_id})=>{
                            return {
                                hotel_puesto_propina_id
                            }
                        })
                    },
                },
            })

            if (update.data?.configurar_esquema_puestos_propinas) {
                refetch()
                showSnackbar({
                    title: "Cambios guardados",
                    text: "Se han guardado los cambios exitosamente.",
                    status: "success",
                })
            } else {
                showSnackbar({
                    title: "Error al guardar cambios",
                    text: "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente.",
                    status: "error",
                })
            }
        } catch(e) {
            console.log(e)
            showSnackbar({
                title: "Error al guardar cambios",
                text: "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente.",
                status: "error",
            })
        } finally {
            setLoad(false)
        }
    }

    return (
        <div className="config-propinas__content">
            <section>
                <p className="config-propinas__title--pagos">Pago de propinas</p>
                <Controller
                    control={control}
                    name={"pago"}
                    render={({field: {value, onChange}})=>(
                        <Switch
                            label={"Pago de propinas en el turno"}
                            description={"Habilitar el pago de propinas por turno, esto se reflejará en todos los cortes."}
                            value={value}
                            onChange={onChange}
                            className="config-propinas__switch"
                        />
                    )}
                />
            </section>
            <p className="config-propinas__title--pagos">Porcentaje de propinas</p>
            <p className="config-propinas__subtitle--pagos">
                Selecciona el área y los puestos que participarán en el esquema de reparto de propinas
            </p>
            <form onSubmit={handleSubmit(onSubmit)} className="config-propinas__form">
                {fields.map((field, index) => {
                    const currentArea = watch(`areas.${index}.area`)

                    const availableRoles = areas[currentArea as keyof typeof areas]?.filter((rol) => {
                        const isSelected = selectedRoles.some(
                            (selectedRole) => selectedRole.rol === rol && selectedRole.index !== index
                        )
                        return !isSelected
                    })

                    const availableAreas = Object.keys(areas).filter(
                        (area) => !isAreaFullySelected(area) || area === currentArea
                    )

                    return (
                        <div key={field.id} style={{ display: "flex", marginBottom: "10px", gap: 24 }}>
                            <Controller
                                name={`areas.${index}.area`}
                                control={control}
                                defaultValue={currentArea}
                                render={({ field: { onChange, value } }) => (
                                    <Dropdown
                                        className="config-propinas__dropdown"
                                        options={
                                            availableAreas?.map((area) => {
                                                return {
                                                    label: area,
                                                    value: area,
                                                }
                                            }) || []
                                        }
                                        value={value}
                                        placeholder={"Selecciona una opción"}
                                        label="Área"
                                        icon="buildingTwo"
                                        iconInOptions={false}
                                        onClick={({ value }) => {
                                            onChange(value)
                                        }}
                                        disabled={isRecepcionista}
                                        errorHintText={fields.length - 1 === index && value === "" ? errorMessage : ""}
                                    />
                                )}
                            />

                            <Controller
                                name={`areas.${index}.rol`}
                                control={control}
                                defaultValue={field.rol}
                                render={({ field: { onChange, value } }) => (
                                    <Dropdown
                                        className="config-propinas__dropdown"
                                        options={
                                            availableRoles?.map((rol) => {
                                                return {
                                                    label: rol,
                                                    value: rol,
                                                }
                                            }) || []
                                        }
                                        value={value}
                                        placeholder={"Selecciona una opción"}
                                        label="Puesto o rol"
                                        icon="SuitcaseFill"
                                        iconInOptions={false}
                                        onClick={({ value }) => {
                                            onChange(value)
                                        }}
                                        disabled={isRecepcionista}
                                        errorHintText={fields.length - 1 === index && value === "" ? errorMessage : ""}
                                    />
                                )}
                            />

                            <Controller
                                name={`areas.${index}.porcentaje`}
                                control={control}
                                defaultValue={field.porcentaje}
                                render={({ field: { onChange, value } }) => (
                                    <InputNumber
                                        className="config-propinas__dropdown"
                                        icon={Icon}
                                        allow0={false}
                                        iconProps={{ name: "Percent", width: 16, height: 16 }}
                                        label="Porcentaje de propina"
                                        placeholder="Agrega un número"
                                        value={value}
                                        onChange={onChange}
                                        disabled={isRecepcionista}
                                        error={
                                            fields.length - 1 === index && value === "" && errorMessage
                                                ? true
                                                : fields.length - 1 === index && error
                                                ? true
                                                : false
                                        }
                                        errorhinttext={error ? error : errorMessage}
                                    />
                                )}
                            />
                            <Icon
                                name="trashFilled"
                                color="#6941C6"
                                onClick={() => (fields.length === 1 ? null : remove(index))}
                                style={{
                                    cursor: fields.length === 1 ? "auto" : "pointer",
                                    opacity: fields.length === 1 ? 0.5 : 1,
                                    marginTop: 25,
                                    pointerEvents: isRecepcionista ? "none" : "auto",
                                }}
                            />
                        </div>
                    )
                })}

                {!isRecepcionista && (
                    <div tabIndex={0} className="modal-mixto__link" onClick={handleAddField}>
                        <Icon name={"plusCircle"} color={"var(--purple-drawer-primario)"} />
                        <span>Agregar otro</span>
                    </div>
                )}
                <Button
                    text={"Guardar cambios"}
                    className="config-propinas__button"
                    type="submit"
                    style={{ marginTop: 30 }}
                    disabled={
                        !lastField?.area || !lastField?.rol || isRecepcionista || !lastField?.porcentaje ? true : false
                    }
                />
            </form>
        </div>
    )
}

export default Pagos